import { CANCELLED_USER_ERROR_CODE } from "../helpers/cancelledAccess";
import type { PublicationModel } from "../types/models";

// The publisher's error codes, provider by provider, in the words the
// calendar tile and the schedule dialog's post card both show. One table,
// so a failed post reads the same wherever it surfaces.

const INSTAGRAM_ERROR_CODE_TO_MESSAGE: { [key: string]: string } = {
  // === Infrastructure / request limits ===
  "2": "Service temporarily unavailable. Please try again.", // System error
  "4": "Application request limit reached. Please wait and try again.", // App-level rate limiting

  // === Permissions ===
  "10": "Missing permission to publish. Re-authorize with the required permissions.", // Permission denied
  "200":
    "Insufficient permission to perform this action. Check app review/scopes.", // Permissions error

  // === Invalid parameters ===
  "100":
    "Invalid parameter. Please check your media URL, caption, or metadata.", // Invalid parameter

  // === Rate limiting / policy ===
  "368": "Temporarily blocked due to policy rules. Please try again later.", // Temporary block
  "613": "Rate limit reached. Please slow down and try again shortly.", // Rate limiting

  // === Authentication (Invalid / expired token) ===
  // These Meta Graph codes are shared by Instagram and Facebook publications,
  // so the wording stays platform-neutral.
  "190":
    "Your account connection expired or is invalid. Please reconnect your account.",

  // --- Common subcodes for 190 (documented by Meta) ---
  "190_460": "The access token is invalid. Please reconnect your account.",
  "190_463": "The access token has expired. Please reconnect your account.",
  "190_464":
    "The access token has been invalidated. Please reconnect your account.",
  "190_467":
    "The access token has been revoked. Please reconnect your account.",
  "190_490": "Authentication expired. Please re-authorize your account.",
};

// Error code to message mapping
const TIKTOK_ERROR_CODE_TO_MESSAGE: { [key: string]: string } = {
  // TikTok API v2.0 error codes
  access_token_invalid:
    "The access token is invalid or not found. Please reconnect your TikTok account.",
  internal_error:
    "TikTok encountered an internal error. Please try again later or contact support.",
  invalid_file_upload:
    "The uploaded file does not meet TikTok's specifications. Please check your file format and try again.",
  invalid_params:
    "One or more request parameters are invalid. Please check your video details and try again.",
  rate_limit_exceeded:
    "TikTok API rate limit exceeded. Please wait a moment and try again.",
  scope_not_authorized:
    "Missing required permissions for this action. Please reconnect your TikTok account with proper authorization.",
  scope_permission_missed:
    "Access token is missing required permissions. Please reconnect your TikTok account.",

  // TikTok Content Posting API error codes (legacy)
  file_format_check_failed:
    "Unsupported media format. Please check your video format and try again.",
  duration_check_failed:
    "Video duration doesn't meet requirements. Please adjust the video length.",
  frame_rate_check_failed:
    "Unsupported frame rate. Please use a supported frame rate for your video.",
  picture_size_check_failed:
    "Video resolution doesn't meet requirements. Please adjust the video size.",
  video_pull_failed:
    "Failed to download video from the provided URL. Please check if the URL is accessible and try again.",
  photo_pull_failed:
    "Failed to download photo from the provided URL. Please check if the URL is accessible and try again.",
  publish_cancelled: "Publishing was cancelled.",
  auth_removed:
    "TikTok access was removed during processing. Please reconnect your account.",
  spam_risk_too_many_posts:
    "Too many posts created in the last 24 hours. Please try posting from the TikTok mobile app or wait before posting again.",
  spam_risk_user_banned_from_posting:
    "Your account has been restricted from posting. Please contact TikTok support.",
  spam_risk_text:
    "The post description contains prohibited content. Please revise your text and try again.",
  spam_risk:
    "Post was flagged as potentially risky content. Please review your content and try again.",
};

// LinkedIn Publishing — Officially documented HTTP status codes and (optional) serviceErrorCode
const LINKEDIN_ERROR_CODE_TO_MESSAGE: { [key: string]: string } = {
  // === Authentication & Authorization ===
  "401":
    "Unauthorized. The access token is missing, invalid, expired, or revoked. Please authenticate again.",

  // === Permissions & Access ===
  "403":
    "Forbidden. Insufficient permissions to perform this action. Please ensure proper scopes and access.",

  // === Bad Request / Input ===
  "400":
    "Bad Request. The request syntax is invalid or malformed. Please check your input parameters.",

  // === Resource Errors ===
  "404": "Not Found. The resource you're trying to access does not exist.",

  // === Method Errors ===
  "405":
    "Method Not Allowed. The HTTP method used is not supported for this endpoint.",

  "411": "Length Required. The request must specify a Content-Length header.",

  "426":
    "Upgrade Required. The API version you're using is deprecated or missing. Please update the version header.",

  // === Rate Limiting ===
  "429":
    "Too Many Requests. You've hit the rate limit. Please slow down and retry later.",

  // === Server Errors ===
  "500":
    "Internal Server Error. LinkedIn experienced an error. Please retry or contact support if persistent.",

  "504":
    "Gateway Timeout. LinkedIn is taking too long to process the request. Please retry.",
};

// KLAP-specific error code to message mapping
const KLAP_ERROR_CODE_TO_MESSAGE: { [key: string]: string } = {
  export_failed: "Video export failed. Please try again.",
  timeout: "Request timed out. Please try again.",
  oauth_token_not_found:
    "OAuth token not found. Please reconnect your account.",
  profile_not_found: "Profile not found. Please check your account settings.",
  invalid_provider:
    "Invalid social media provider. Please select a valid platform.",
  media_container_failed: "Media container creation failed. Please try again.",
  media_publish_failed: "Media publishing failed. Please try again.",
  media_container_timeout:
    "Media container creation timed out. Please try again.",
  video_download_failed:
    "Video download failed. Please check your video source.",
  upload_failed: "Upload failed. Please try again.",
  rate_limited: "Rate limit exceeded. Please wait before trying again.",
  unknown: "An unknown error occurred. Please try again.",
  // klap-api's publishing rule (helpers/cancelled_user_gating.ts).
  subscription_ended:
    "Your Klap subscription has ended, so this post wasn't published. Resubscribe, then retry it.",
};

// Error code to message mapping
export const ERROR_CODE_TO_MESSAGE: { [key: string]: string } = {
  ...TIKTOK_ERROR_CODE_TO_MESSAGE,
  ...INSTAGRAM_ERROR_CODE_TO_MESSAGE,
  ...LINKEDIN_ERROR_CODE_TO_MESSAGE,
  ...KLAP_ERROR_CODE_TO_MESSAGE,
};

/**
 * What to tell the user about a failed post: the code's message, or the
 * generic line when the publisher recorded a code this table does not know
 * (a new provider error, or a null code on an old row). Null for any post
 * that is not failed, so callers can branch on it directly.
 *
 * `includeRetryHint` (default true): calendar still offers Retry now, so
 * "Please try again" stays. Schedule stages the plan and has no Retry —
 * pass false to drop that CTA and append where to go next instead, except
 * for subscription_ended, whose message already says to resubscribe.
 */
export const getPublicationErrorMessage = (
  publication: Pick<PublicationModel, "status" | "errorCode">,
  options?: { includeRetryHint?: boolean },
): string | null => {
  if (publication.status !== "failed") return null;
  const message =
    (publication.errorCode && ERROR_CODE_TO_MESSAGE[publication.errorCode]) ||
    "An unknown error occurred";
  if (
    options?.includeRetryHint === false &&
    publication.errorCode !== CANCELLED_USER_ERROR_CODE
  ) {
    return `${stripRetryHint(message)} ${SCHEDULE_FAILED_NEXT_STEP}`;
  }
  return message;
};

/** Schedule has no Retry; point at reschedule here or Retry on Calendar. */
const SCHEDULE_FAILED_NEXT_STEP =
  "You can reschedule, or retry from Calendar.";

/** Drop trailing "try again / retry" CTAs; keep the failure reason. */
const stripRetryHint = (message: string): string => {
  const stripped = message
    .replace(
      /\s*Please try again( later)?( or contact support)?\.?\s*$/i,
      "",
    )
    .replace(
      /\s*Please wait( a moment)? and try again( shortly)?\.?\s*$/i,
      "",
    )
    .replace(/\s*Please wait before trying again\.?\s*$/i, "")
    .replace(
      /\s*Please retry( later)?( or contact support if persistent)?\.?\s*$/i,
      "",
    )
    .replace(/\s*Please slow down and (try again shortly|retry later)\.?\s*$/i, "")
    .trim();
  if (!stripped) return message;
  return /[.!?]$/.test(stripped) ? stripped : `${stripped}.`;
};
